import { type RefObject, useCallback, useId, useState } from "react";
import type { TextInput } from "react-native";

import { useControlledField } from "../../utils/useControlledField";
import type { FieldValidation } from "./FieldShell";

/** Text-color variant map shared by the Input and TextArea styled controls. */
export const fieldTextVariants = {
  validation: {
    none: {
      color: "$textDefault",
      placeholderTextColor: "$textSubtle",
    },
    error: {
      color: "$textNegative",
      placeholderTextColor: "$textNegative",
    },
    success: {
      color: "$textDefault",
      placeholderTextColor: "$textSubtle",
    },
  },
} as const;

/**
 * Focus handler typed by the control. `FocusEvt` is inferred from the owner's
 * `onFocus`/`onBlur` so the returned handlers match the styled control's
 * (Tamagui-flavoured) signature without a cast.
 */
type FocusHandler<FocusEvt> = (event: FocusEvt) => void;

export type UseTextFieldShellParams<FocusEvt> = {
  idPrefix: string;
  id?: string;
  label?: string;
  validation: FieldValidation;
  disabled: boolean;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  helperText?: string;
  accessibilityLabel?: string;
  onChangeText?: (next: string) => void;
  onFocus?: FocusHandler<FocusEvt>;
  onBlur?: FocusHandler<FocusEvt>;
  /** Ref to the underlying control so a frame press can focus it (native). */
  controlRef: RefObject<TextInput | null>;
};

export type UseTextFieldShellResult<FocusEvt> = {
  /** Props to spread onto the underlying text control. The control's `value` /
   *  `defaultValue` stay owner-supplied — Tamagui types them as a broad HTML
   *  union the component already holds, so the hook only tracks a stringified
   *  copy for label-floating + length. */
  controlProps: {
    id: string;
    placeholder: string | undefined;
    onChangeText: (next: string) => void;
    onFocus: FocusHandler<FocusEvt>;
    onBlur: FocusHandler<FocusEvt>;
    tabIndex: number;
    "aria-label": string | undefined;
    "aria-disabled": boolean;
    "aria-invalid": boolean;
    "aria-describedby": string | undefined;
    "aria-labelledby": string | undefined;
  };
  /** Chrome props for `<FieldShell>`. */
  shellProps: {
    labelFloated: boolean;
    focused: boolean;
    labelId: string;
    helperTextId: string;
    currentLength: number;
    onPress: () => void;
  };
  /** Current value (controlled or internal), never undefined. */
  currentValue: string;
};

/**
 * Wires the controlled/uncontrolled value, focus state, accessibility ids, and
 * floating-label logic that Input and TextAreaInput share. Returns ready-to-spread
 * `controlProps` (for the text control) and `shellProps` (for `<FieldShell>`).
 *
 * `controlProps.placeholder` is the *effective* placeholder: suppressed while the
 * resting label occupies the placeholder position, restored once the label floats.
 */
export const useTextFieldShell = <FocusEvt>({
  idPrefix,
  id: providedId,
  label,
  validation,
  disabled,
  value,
  defaultValue,
  placeholder,
  helperText,
  accessibilityLabel,
  onChangeText,
  onFocus,
  onBlur,
  controlRef,
}: UseTextFieldShellParams<FocusEvt>): UseTextFieldShellResult<FocusEvt> => {
  const uniqueId = useId();
  const fieldId = providedId ?? `${idPrefix}-${uniqueId}`;
  const helperTextId = `${fieldId}-helper`;
  const labelId = `${fieldId}-label`;

  const [isFocused, setIsFocused] = useState(false);

  const field = useControlledField<string>({
    value,
    defaultValue,
    onChange: onChangeText,
  });
  const currentValue = field.value ?? "";
  const hasValue = Boolean(currentValue);

  const isActiveFocus = isFocused && !disabled;
  const isLabelFloated = Boolean(label) && (isFocused || hasValue);
  const isInvalid = validation === "error";

  const handleFocus = useCallback<FocusHandler<FocusEvt>>(
    event => {
      if (!disabled) {
        setIsFocused(true);
        onFocus?.(event);
      }
    },
    [disabled, onFocus],
  );

  const handleBlur = useCallback<FocusHandler<FocusEvt>>(
    event => {
      setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleFramePress = useCallback(() => {
    if (!disabled) {
      controlRef.current?.focus();
    }
  }, [disabled, controlRef]);

  // When resting the label IS the placeholder, suppress the control's own
  // placeholder so the two don't stack.
  const effectivePlaceholder = label
    ? isLabelFloated
      ? placeholder
      : undefined
    : placeholder;

  return {
    controlProps: {
      id: fieldId,
      placeholder: effectivePlaceholder,
      onChangeText: field.setValue,
      onFocus: handleFocus,
      onBlur: handleBlur,
      tabIndex: disabled ? -1 : 0,
      "aria-label": accessibilityLabel ?? label,
      "aria-disabled": disabled,
      "aria-invalid": isInvalid,
      "aria-describedby": helperText ? helperTextId : undefined,
      "aria-labelledby": label ? labelId : undefined,
    },
    shellProps: {
      labelFloated: isLabelFloated,
      focused: isActiveFocus,
      labelId,
      helperTextId,
      currentLength: currentValue.length,
      onPress: handleFramePress,
    },
    currentValue,
  };
};
